/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

const SYNTHETIC_STATE_FLAG = '__fluxer_synth';
const MAX_SYNTHETIC_DEPTH = 6;

export interface SyntheticHistoryState {
	[SYNTHETIC_STATE_FLAG]: true;
	depth: number;
}

export function isSyntheticHistoryState(state: unknown): state is SyntheticHistoryState {
	return typeof state === 'object' && state !== null && (state as Record<string, unknown>)[SYNTHETIC_STATE_FLAG] === true;
}

/**
 * Pure-syntactic derivation of a parent path stack from a deep-link URL.
 * Always returns at least one entry: the deepest derivable parent.
 *
 * Examples:
 *  /channels/@me/123/456     -> ["/channels/@me", "/channels/@me/123"]
 *  /channels/@favorites/123  -> ["/channels/@me", "/channels/@favorites"]
 *  /channels/9/8/7           -> ["/channels/9", "/channels/9/8"]
 *  /channels/9/8             -> ["/channels/9"]
 *  /channels/9               -> []
 */
export function derivePathStack(path: string): Array<string> {
	const segments = path.split('?')[0].split('#')[0].split('/').filter((s) => s.length > 0);
	if (segments[0] !== 'channels' || segments.length < 2) return [];

	const root = segments[1];
	if (root === '@me' || root === '@favorites' || root === '@discover') {
		const stack: Array<string> = [];
		if (root !== '@me') stack.push('/channels/@me');
		stack.push(`/channels/${root}`);
		if (segments.length >= 3) stack.push(`/channels/${root}/${segments[2]}`);
		return stack.slice(0, -1);
	}

	const stack: Array<string> = [`/channels/${root}`];
	if (segments.length >= 3) stack.push(`/channels/${root}/${segments[2]}`);
	return stack.slice(0, -1);
}

/**
 * Synthesize a parent history stack for deep-link entries so that swipe-back /
 * back-button has somewhere to land. Must run synchronously at boot, BEFORE
 * any awaits, otherwise iOS will drop the entries (>500ms after gesture rule).
 */
export function bootstrapSyntheticHistory(): void {
	if (typeof window === 'undefined' || !window.history) return;
	if (isSyntheticHistoryState(window.history.state)) return;
	if (window.history.length > 1) return;

	const path = window.location.pathname + window.location.search + window.location.hash;
	const parents = derivePathStack(window.location.pathname).slice(-MAX_SYNTHETIC_DEPTH);
	if (parents.length === 0) return;

	const deepestParent = parents[0];
	window.history.replaceState({[SYNTHETIC_STATE_FLAG]: true, depth: 0} satisfies SyntheticHistoryState, '', deepestParent);
	for (let i = 1; i < parents.length; i++) {
		window.history.pushState({[SYNTHETIC_STATE_FLAG]: true, depth: i} satisfies SyntheticHistoryState, '', parents[i]);
	}
	window.history.pushState(null, '', path);
}
